<template>
  <WithPaginationAndFilters
    :is-raised="false"
    :is-loading="isLoading"
    :data="campaigns"
  >
    <template slot-scope="{ data, sortChange, filterChange }">
      <ElTable
        :data="data"
        show-overflow-tooltip
        stripe
        :row-style="{ cursor: 'pointer' }"
        row-key="id"
        show-summary
        :summary-method="summaryMethod"
        @sort-change="sortChange"
        @filter-change="filterChange"
        @row-click="goToCampaign"
      >
        <ElTableColumn
          width="200"
          prop="name"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.NAME')"
          fixed
        />
        <ElTableColumn
          width="150"
          prop="packageName"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.PACKAGE_NAME')"
        />
        <ElTableColumn
          width="130"
          :label="$t('ADVERTISEMENT.CAMPAIGN_FINANCE.REMAINING_BUDGET')"
          prop="budget"
          sortable
        >
          <template slot-scope="{ row: { budget } }">
            {{ priceFormat(budget) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          width="130"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.SPENT_BUDGET')"
          prop="totalCost"
          sortable
        >
          <template slot-scope="{ row: { totalCost } }">
            {{ priceFormat(totalCost) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          width="100"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.START_DATE')"
          prop="startDate"
        >
          <template slot-scope="{ row: { startDate } }">
            {{ translateDate(startDate) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          width="100"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.END_DATE')"
          prop="endDate"
        >
          <template slot-scope="{ row: { endDate } }">
            {{ translateDate(endDate) }}
          </template>
        </ElTableColumn>
        <ColumnWithHelp
          :width="100"
          :title="$t('ADVERTISEMENT.CAMPAIGNS.STATUS')"
          :content="$t('ADVERTISEMENT.CAMPAIGNS.STATUS_SEARCH_DESCRIPTION')"
          :label="$t('ADVERTISEMENT.CAMPAIGNS.STATUS')"
        >
          <template slot-scope="{ row: { status } }">
            {{ $t(translateCampaignStatus(status)) }}
          </template>
        </ColumnWithHelp>
        <ElTableColumn
          width="150"
          sortable
          :label="$t('ADVERTISEMENT.CAMPAIGNS.INSTALL_COUNT')"
          prop="installCount"
        >
          <template slot-scope="{ row: { installCount } }">
            {{ priceFormat(installCount) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          width="120"
          sortable
          :label="$t('ADVERTISEMENT.CAMPAIGNS.IMPRESSION_COUNT')"
          prop="impressionCount"
        >
          <template slot-scope="{ row: { impressionCount } }">
            {{ priceFormat(impressionCount) }}
          </template>
        </ElTableColumn>
        <ElTableColumn
          width="150"
          sortable
          :label="$t('ADVERTISEMENT.CAMPAIGNS.AVG_INSTALL_COST')"
          prop="avgInstallCost"
        >
          <template slot-scope="{ row: { avgInstallCost } }">
            {{ priceFormat(avgInstallCost) }}
          </template>
        </ElTableColumn>
      </ElTable>
    </template>
  </WithPaginationAndFilters>
</template>

<script>
import WithPaginationAndFilters from '@@/common/paginations/WithPaginationAndFilters';
import ColumnWithHelp from '@@/advertisement/common/ColumnWithHelp';

import calculateSummary from '@/utils/calculateSummary';
import { sum } from '@/utils/arrayAggregation';

import { format as priceFormat } from '@/utils/number';
import { dateFormat } from '@/utils/date';
import lang from '@/mixins/lang';

import translateCampaignStatus from '../../../common/utility/translators/translateCampaignStatus';

import { AD_CAMPAIGN_CLICK } from '@/constants/gtm-events';

export default {
  name: 'SearchAdsCampaignsListTable',
  components: {
    WithPaginationAndFilters,
    ColumnWithHelp,
  },
  mixins: [lang],
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    campaigns: {
      type: Array,
      required: true,
    },
  },
  methods: {
    translateCampaignStatus,
    priceFormat,
    summaryMethod({ columns, data }) {
      const columnsBehaviour = {
        name: () => this.$t('SUM'),
        totalCost: sum,
        installCount: sum,
        impressionCount: sum,
        avgInstallCost: (_, allColumnsData) => {
          const totalInstallCost = allColumnsData.map(
            val => val.totalInstallCost,
          );
          const installCounts = allColumnsData.map(val => val.installCount);

          const installCountsSum = sum(installCounts);
          const totalInstallCostSum = sum(totalInstallCost);

          return Math.round(
            installCountsSum ? totalInstallCostSum / installCountsSum : 0,
          );
        },
      };
      return calculateSummary({ columns, data, columnsBehaviour });
    },
    translateDate(date) {
      let toFormat = 'DD/MM/YYYY';

      if (this.lang === 'fa') {
        toFormat = 'jYYYY/jMM/jDD';
      }

      return dateFormat()(toFormat)(date);
    },
    goToCampaign({ id: campaignId }) {
      this.$gtm.trackEvent(AD_CAMPAIGN_CLICK);

      this.$router.push({
        name: 'search-ads-campaign-info',
        params: { campaignId },
      });
    },
  },
};
</script>
